/**
 * Renders a blue circle indicator on the right side of a target element
 * @param {string} selector - CSS selector for the target element
 * @param {Object} options - Optional configuration
 * @param {string} options.color - Circle color (default: '#3b82f6' - blue)
 * @param {number} options.size - Circle size in pixels (default: 16)
 * @param {number} options.distance - Distance from target in pixels (default: 8)
 * @param {number} options.animationDuration - Animation duration in ms (default: 1000)
 */
export function pointArrowsToElement(
  selector: string,
  options: {
    color?: string;
    size?: number;
    distance?: number;
    animationDuration?: number;
  } = {}
) {
  const {
    color = "#3b82f6", // Blue color
    size = 16,
    distance = 8,
    animationDuration = 1000,
  } = options;

  // Get the target element
  const target = document.querySelector(selector);

  if (!target) {
    console.error(`Element not found for selector: ${selector}`);
    return null;
  }

  // Remove any existing indicator containers and restore previous element styles
  const existingContainer = document.getElementById("arrow-pointer-container");
  if (existingContainer) {
    const previousElement = existingContainer.dataset.targetElement;
    if (previousElement) {
      const prevTarget = document.querySelector(previousElement);
      if (prevTarget) {
        const originalStyles = existingContainer.dataset.originalStyles;
        if (originalStyles) {
          try {
            const styles = JSON.parse(originalStyles);
            Object.keys(styles).forEach((key) => {
              (prevTarget as HTMLElement).style[key as any] = styles[key];
            });
          } catch (e) {
            // Fallback: just remove the border
            (prevTarget as HTMLElement).style.outline = "";
            (prevTarget as HTMLElement).style.border = "";
          }
        }
      }
    }
    existingContainer.remove();
  }

  // Store original styles of the target element
  const targetElement = target as HTMLElement;
  const originalStyles = {
    outline: targetElement.style.outline || "",
    border: targetElement.style.border || "",
    boxSizing: targetElement.style.boxSizing || "",
  };

  // Create container for the circle indicator
  const container = document.createElement("div");
  container.id = "arrow-pointer-container";
  container.dataset.targetElement = selector;
  container.dataset.originalStyles = JSON.stringify(originalStyles);
  container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 999999;
      `;

  // Add styles for animation
  const style = document.createElement("style");
  style.textContent = `
        @keyframes circlePulse {
          0%, 100% { transform: translateY(-50%) scale(1); opacity: 1; }
          50% { transform: translateY(-50%) scale(1.2); opacity: 0.8; }
        }
        .pointing-circle {
          position: absolute;
          border-radius: 50%;
          background-color: ${color};
          width: ${size}px;
          height: ${size}px;
          animation: circlePulse ${animationDuration}ms ease-in-out infinite;
        }
      `;
  container.appendChild(style);

  // Create the blue circle
  const circle = document.createElement("div");
  circle.className = "pointing-circle";

  // Add circle to container
  container.appendChild(circle);

  // Function to update circle position (on the right side of the element)
  function updatePositions() {
    const rect = target!.getBoundingClientRect();
    const centerY = rect.top + rect.height / 2;

    // Position circle on the right side of the element
    circle.style.left = `${rect.right + distance}px`;
    circle.style.top = `${centerY}px`;
  }

  // Initial position update
  document.body.appendChild(container);
  updatePositions();

  // Update on scroll and resize
  const updateHandler = () => requestAnimationFrame(updatePositions);
  window.addEventListener("scroll", updateHandler, true);
  window.addEventListener("resize", updateHandler);

  // Return control object
  return {
    remove: () => {
      // Restore original styles of the target element
      try {
        const styles = JSON.parse(container.dataset.originalStyles || "{}");
        Object.keys(styles).forEach((key) => {
          targetElement.style[key as any] = styles[key];
        });
      } catch (e) {
        // Fallback: just remove the border
        targetElement.style.outline = "";
        targetElement.style.outlineOffset = "";
        targetElement.style.border = "";
      }

      container.remove();
      window.removeEventListener("scroll", updateHandler, true);
      window.removeEventListener("resize", updateHandler);
    },
    updatePositions,
    setColor: (newColor: string) => {
      circle.style.backgroundColor = newColor;
    },
  };
}
